import { useQuery } from '@tanstack/react-query';

import {
  type User,
  type UserFilters,
  UserSchema,
  type UserTermsAndConditionsAcceptance,
  UserTermsAndConditionsAcceptanceSchema,
  UserTermsAndConditionsAcceptancesSchema,
  UsersSchema,
} from '@mai/types';

import { apiClient } from './axios';

import { logger } from '@utils/logger';

export const useUserQuery = (userId: string | null | undefined) => {
  return useQuery({
    queryKey: ['users', userId],
    queryFn: async () => {
      const response = await apiClient.get<User>(`/users/${userId}`);
      const validatedData = UserSchema.safeParse(response.data);
      if (!validatedData.success) {
        logger.warn('failed to validate user data', {
          error: validatedData.error,
        });
      }
      return validatedData.data;
    },
    enabled: !!userId,
  });
};

export const useUsersQuery = (params: UserFilters, enabled: boolean) => {
  return useQuery({
    queryKey: ['users', params],
    queryFn: async () => {
      const response = await apiClient.get<User[]>('/users', { params });
      const validatedData = UsersSchema.safeParse(response.data);
      if (!validatedData.success) {
        logger.warn('failed to validate users data', {
          error: validatedData.error,
        });
      }
      return validatedData.data;
    },
    enabled,
  });
};

export const useUserTermsAndConditionsAcceptancesQuery = (
  userId: string | null | undefined,
) => {
  return useQuery({
    queryKey: ['userTermsAndConditionsAcceptances', userId],
    queryFn: async () => {
      const response = await apiClient.get<UserTermsAndConditionsAcceptance[]>(
        `/users/${userId}/terms-and-conditions-acceptance`,
      );
      const validatedData = UserTermsAndConditionsAcceptancesSchema.safeParse(
        response.data,
      );
      if (!validatedData.success) {
        logger.warn(
          'failed to validate user terms and conditions acceptances data',
          {
            error: validatedData.error,
          },
        );
      }
      return validatedData.data;
    },
    enabled: !!userId,
  });
};

export const acceptUserTermsAndConditions = async (userId: string) => {
  const response = await apiClient.post<UserTermsAndConditionsAcceptance>(
    `/users/${userId}/accept-terms-and-conditions`,
  );
  const validatedData = UserTermsAndConditionsAcceptanceSchema.safeParse(
    response.data,
  );
  if (!validatedData.success) {
    logger.warn(
      'failed to validate user terms and conditions acceptance data',
      {
        error: validatedData.error,
      },
    );
  }
  return validatedData.data;
};
